import { MagnifyingGlass } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { useId, useMemo, useRef, useState } from 'react'
import { request } from '../api/client'
import type { Customer, PageData } from '../api/types'

export type CustomerFilterKind = 'customer' | 'source-customer' | 'partner-funder'

interface CustomerFilterOption {
  id: string | number
  name: string
  nameInitial?: string
  type?: string
  visibility?: string
  phoneMasked?: string
  regionLabel?: string
}

interface CustomerFilterPickerProps {
  kind: CustomerFilterKind
  value: string
  onChange: (value: string) => void
}

const CUSTOMER_TYPES = ['ALL', 'GUEST', 'PEER'] as const
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

function customerInitial(item: CustomerFilterOption) {
  const initial = (item.nameInitial || item.name.trim().slice(0, 1)).toUpperCase()
  return /^[A-Z]$/.test(initial) ? initial : '#'
}

function customerTypeLabel(type?: string) {
  return type === 'PEER' ? '同行' : '客人'
}

export function CustomerFilterPicker({ kind, value, onChange }: CustomerFilterPickerProps) {
  const [keyword, setKeyword] = useState('')
  const [type, setType] = useState<(typeof CUSTOMER_TYPES)[number]>('ALL')
  const radioName = `customer-filter-${useId()}`
  const groupRefs = useRef(new Map<string, HTMLDivElement>())
  const queryParams = { scope: 'ALL', type: type === 'ALL' ? undefined : type, keyword: keyword.trim() || undefined, pageNo: 1, pageSize: 100 }
  const endpoint = kind === 'customer' ? '/luxury/customer/page' : `/luxury/product/catalog/${kind}/page`
  const options = useQuery({
    queryKey: ['customer-filter-picker', kind, type, keyword],
    queryFn: () => request<PageData<CustomerFilterOption>>({ url: endpoint, params: queryParams }),
  })
  const details = useQuery({
    queryKey: ['customer-filter-picker-details', kind, type, keyword],
    enabled: kind !== 'customer',
    queryFn: () => request<PageData<Customer>>({ url: '/luxury/customer/page', params: queryParams }),
  })
  const items = useMemo(() => {
    const detailMap = new Map((details.data?.list || []).map((item) => [String(item.id), item]))
    return (options.data?.list || []).map((item) => ({ ...detailMap.get(String(item.id)), ...item }))
      .sort((left, right) => {
        const leftInitial = customerInitial(left)
        const rightInitial = customerInitial(right)
        if (leftInitial === rightInitial) return left.name.localeCompare(right.name, 'zh-CN')
        if (leftInitial === '#') return 1
        if (rightInitial === '#') return -1
        return leftInitial.localeCompare(rightInitial, 'en')
      })
  }, [details.data?.list, options.data?.list])
  const groups = useMemo(() => {
    const grouped = new Map<string, CustomerFilterOption[]>()
    for (const item of items) {
      const initial = customerInitial(item)
      grouped.set(initial, [...(grouped.get(initial) || []), item])
    }
    return [...grouped.entries()]
  }, [items])
  const availableLetters = new Set(groups.map(([letter]) => letter))
  const loading = options.isLoading || (kind !== 'customer' && details.isLoading)
  const failed = options.isError

  const switchType = (next: (typeof CUSTOMER_TYPES)[number]) => setType(next)
  const jumpToLetter = (letter: string) => groupRefs.current.get(letter)?.scrollIntoView({ block: 'start' })

  return <div className="lks-customer-picker is-create-hidden" aria-label="客户选择">
    <label className="lks-customer-search"><MagnifyingGlass aria-hidden="true" /><input autoFocus type="search" value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="搜索姓名、手机号或公司" autoComplete="off" /></label>
    <div className="lks-customer-tabs" role="tablist" aria-label="客户类型">
      {CUSTOMER_TYPES.map((item) => <button className={type === item ? 'is-active' : ''} key={item} role="tab" aria-selected={type === item} type="button" onClick={() => switchType(item)}>{item === 'ALL' ? '全部' : item === 'GUEST' ? '客人' : '同行'}</button>)}
    </div>
    <div className="lks-customer-directory-shell">
      <div className="lks-customer-directory" aria-label="客户列表">
        {loading && <div className="lks-customer-empty"><strong>正在加载客户…</strong><span>请稍候</span></div>}
        {failed && <div className="lks-customer-empty"><strong>客户加载失败</strong><button type="button" onClick={() => options.refetch()}>重新加载</button></div>}
        {!loading && !failed && groups.map(([letter, customers]) => <section className="lks-customer-group" key={letter}>
          <div className="lks-customer-letter" data-customer-letter={letter} ref={(node) => { if (node) groupRefs.current.set(letter, node); else groupRefs.current.delete(letter) }}>{letter}</div>
          {customers.map((item) => {
            const selected = value === String(item.id)
            const peer = item.type === 'PEER'
            return <label className="lks-customer-row" key={item.id}>
              <span className={`lks-customer-avatar ${peer ? 'peer' : 'guest'}`}>{item.name.slice(0, 1)}</span>
              <span className="lks-customer-copy"><span><strong>{item.name}</strong><em className={`lks-customer-badge ${peer ? 'peer' : 'guest'}`}>{customerTypeLabel(item.type)}</em>{item.visibility === 'PUBLIC' && <em className="lks-customer-badge public">公共</em>}</span><small>{item.phoneMasked || '手机号未填'} · {item.regionLabel || '省市未填'}</small></span>
              <input aria-label={`选择${item.name}`} type="radio" name={radioName} value={String(item.id)} checked={selected} onChange={() => onChange(String(item.id))} />
            </label>
          })}
        </section>)}
        {!loading && !failed && !items.length && <div className="lks-customer-empty"><strong>没有匹配的客户</strong><span>请更换关键词或客户类型</span></div>}
      </div>
      <nav className="lks-customer-letter-index" aria-label="客户首字母索引">
        {LETTERS.map((letter) => <button key={letter} type="button" disabled={!availableLetters.has(letter)} aria-label={`跳转到 ${letter} 组客户`} onClick={() => jumpToLetter(letter)}>{letter}</button>)}
      </nav>
    </div>
  </div>
}
